import { useTranslation } from 'react-i18next';
import { color as fplColor } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { EL_COLORS } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { ShareExposedNote, ShareScopeNoteLine } from '@/components/Recipe/FlowComponents/Share/ShareScopeNote';

interface ToolShareScopeBannerProps {
  /** The workflow's "show workflow" opt-in: its viewers include everyone who can see the tool. */
  exposesWorkflow: boolean;
}

/**
 * What sharing the tool does to the workflow, under the modal's header (design 86451:55709 and
 * 86451:55026). Without the opt-in the two access lists are separate, so the note points at the
 * canvas Share. With it, every invite here also opens the workflow, so the banner says so instead.
 */
export function ToolShareScopeBanner({ exposesWorkflow }: ToolShareScopeBannerProps) {
  const { t } = useTranslation();
  const isFpl = useFplMigrationFlag();

  return (
    <Box
      sx={{
        // The header above rules itself off, so this only draws the line under itself.
        ...(isFpl
          ? { boxShadow: `inset 0 -1px ${fplColor.border}` }
          : { borderBottom: `1px solid ${EL_COLORS.BoxBorder}` }),
        ...(exposesWorkflow ? { p: 1.5 } : { px: 1.5, py: 1 }),
      }}
    >
      {!exposesWorkflow ? (
        <ShareScopeNoteLine
          lead={t(I18N_KEYS.TOOL_SHARE.WORKFLOW_SCOPE_NOTE)}
          detail={t(I18N_KEYS.TOOL_SHARE.WORKFLOW_SCOPE_NOTE_ACTION)}
        />
      ) : (
        <ShareExposedNote
          title={t(I18N_KEYS.TOOL_SHARE.WORKFLOW_EXPOSED_NOTE)}
          detail={t(I18N_KEYS.TOOL_SHARE.WORKFLOW_EXPOSED_NOTE_DETAIL)}
        />
      )}
    </Box>
  );
}
